import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { theme } from "../theme";
import { rgba } from "polished";

const TooltipWrapper = styled("div")({
  position: "relative",
  display: "inline-block",
});

const TooltipContainer = styled("div")(({ size, color, positionStyles }) => ({
  position: "absolute",
  padding: size === "sm" ? "0.0625rem 0.25rem" : size === "lg" ? "0.25rem 0.5rem" : "0.125rem 0.375rem",
  fontSize: size === "sm" ? "0.625rem" : size === "lg" ? "0.875rem" : "0.75rem",
  color: theme.colors.light.light,
  backgroundColor:
    rgba(theme.colors[color]?.main || theme.colors.default.main, 0.85),
  borderRadius: "4px",
  whiteSpace: "nowrap",
  opacity: 0,
  transition: "opacity 0.2s ease-in-out",
  pointerEvents: "none",
  ...positionStyles,
}));

export const Tooltip = ({
  children,
  title,
  position = "top",
  backgroundColor,
  color,
  size = "md",
  delay = 200,
  ...props
}) => {
  const [visible, setVisible] = useState(false);
  const [positionStyles, setPositionStyles] = useState({});
  const tooltipRef = useRef(null);

  const showTooltip = () => {
    setTimeout(() => setVisible(true), delay);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  useEffect(() => {
    if (visible && tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const styles = getPositionStyles(tooltipRect, position);
      setPositionStyles(styles);
    }
  }, [visible, position]);

  const getPositionStyles = (rect, position) => {
    switch (position) {
      case "top":
        return {
          bottom: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          marginBottom: "8px",
        };
      case "right":
        return {
          left: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginLeft: "8px",
        };
      case "bottom":
        return {
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          marginTop: "8px",
        };
      case "left":
        return {
          right: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginRight: "8px",
        };
      default:
        return {};
    }
  };

  return (
    <TooltipWrapper
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      {...props}
    >
      {children}
      {visible && title && (
        <TooltipContainer
          ref={tooltipRef}
          color={color}
          backgroundColor={backgroundColor}
          size={size}
          positionStyles={positionStyles}
          style={{ opacity: visible ? 1 : 0 }}
        >
          {title}
        </TooltipContainer>
      )}
    </TooltipWrapper>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  position: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  backgroundColor: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  delay: PropTypes.number,
};

Tooltip.defaultProps = {
  position: "top",
  backgroundColor: "rgba(97, 97, 97, 0.9)",
  color: "#fff",
  size: "md",
  delay: 200,
};

export default Tooltip;
